import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../context/authContext';
import karaokeService from '../services/karaoke.service';
import StudioRecorder from '../components/karaoke/StudioRecorder';
import '../components/karaoke/karaoke.css';

/**
 * The recording studio, on its own page.
 *
 * `/fs` is the library — browse songs, hear the original, look at covers. Singing is
 * a different activity: it wants the whole screen, no tabs to lose your place in, and
 * a URL you can come back to. So the studio is `/studio/:key`, and `/fs` links into it.
 *
 * The stage is deliberately dark. Everything else on this site is a white card on a
 * light background, which is right for a feed and wrong here — the live wave and the
 * score are the only things that should be competing for attention while someone is
 * singing.
 */
export default function KaraokeStudio() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);

  const [song, setSong] = useState(null);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(!!key);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!key) {
      // No song chosen — show the picker rather than a dead end.
      setLoading(true);
      karaokeService.listSongs()
        .then((d) => setSongs(d.songs || []))
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
      return;
    }
    setLoading(true);
    karaokeService.getSong(key)
      .then((d) => setSong(d.song))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [key]);

  const title = song ? `Sing ${song.title}` : 'Karaoke Studio';

  return (
    <div className="main-content right-chat-active">
      <Helmet>
        <title>{`${title} | Geet Suhane`}</title>
        <meta
          name="description"
          content="Sing along to GeetSuhane originals and get scored live on pitch, timing and rhythm."
        />
      </Helmet>

      <div className="middle-sidebar-bottom">
        <div className="middle-sidebar-left">
          <div className="gs-stagepage">

            <div className="gs-stagepage__bar">
              <button
                type="button"
                className="gs-stagepage__back"
                onClick={() => (song ? navigate('/studio') : navigate('/fs'))}
              >
                ‹ {song ? 'Songs' : 'Karaoke'}
              </button>
              <span className="gs-stagepage__brand">🎤 Studio</span>
            </div>

            {loading && <p className="gs-stagepage__msg">Loading…</p>}
            {error && <p className="gs-stagepage__msg is-error">{error}</p>}

            {/* ---------------------------------------------------- picker */}
            {!key && !loading && (
              <>
                <h1 className="gs-stagepage__title">Pick a song to sing</h1>
                <p className="gs-stagepage__sub">
                  You'll be scored live on pitch, timing and how much of the song you sing.
                  Headphones give the fairest score.
                </p>
                <div className="gs-stagepage__songs">
                  {songs.map((s) => (
                    <Link key={s.id} to={`/studio/${s.slug || s.id}`} className="gs-stagecard">
                      <span
                        className="gs-stagecard__art"
                        style={s.art_url ? { backgroundImage: `url(${s.art_url})` } : undefined}
                      />
                      <span className="gs-stagecard__meta">
                        <strong>{s.title}</strong>
                        <small>{s.artist || 'GeetSuhane Originals'}</small>
                      </span>
                      <span className="gs-stagecard__go">Sing ›</span>
                    </Link>
                  ))}
                  {!songs.length && !error && (
                    <p className="gs-stagepage__msg">No songs are ready to sing yet.</p>
                  )}
                </div>
              </>
            )}

            {/* ----------------------------------------------------- stage */}
            {song && (
              <>
                <div className="gs-stagepage__hero">
                  <span
                    className="gs-stagepage__art"
                    style={song.art_url ? { backgroundImage: `url(${song.art_url})` } : undefined}
                  />
                  <div>
                    <h1>{song.title}</h1>
                    <p>{song.artist || 'GeetSuhane Originals'}</p>
                  </div>
                </div>

                {!currentUser && (
                  <p className="gs-stagepage__msg is-warn">
                    You can sing and see your score, but you'll need to
                    {' '}<Link to="/login">sign in</Link> to save the take.
                  </p>
                )}

                <StudioRecorder song={song} currentUser={currentUser} onSaved={() => {}} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
